// Guide Block Types:
// - "section": { id: string, title: string, content: []}
// - "subsection": { title: string, content: [] }
// - "subdivision": { title: string, content: [] }
// - "text": { data: string | () => JSX }
// - "image": { data: string (url) }
// - "gallery": { images: [{ src, alt, credit: { name, url } }] }
// - "table": { data: [ { row: {} }]}
// - "list": { data: string (newline separated list) }
// - "component": { data: string (key to componentMap) }
// - "inlineLink": { text: string, onClick: fn }

const otherData = {
  id: "other",
  title: "End Game Content",
  category: "Guides",
  slug: "other",
  sections: [
    {
      id: "taskboard",
      title: "Taskboard",
      type: "section",
      content: [
        {
          id: "what-is-taskboard",
          title: "What is the Task Board?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `The Task Board (also known as the Infinifunnel) is one of the main elements of gameplay that earns a player income. Without it, it would be very difficult to earn any coins or $PIXEL.<br/>
              <br/>
              There's no way to sell items to an NPC, so the task board determines the items you can sell for the day (unless you have access to the Marketplace). <br/>
              It resets every 24 hours, and provides a limited amount of tasks depending on your levels.
              <br/>After completing a task, you must wait <b>2 minutes</b> for a new task to appear.`,
            },
          ],
        },
        {
          id: "where-is-taskboard",
          title: "Where is the Infinifunnel?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `The Infinifunnel is located in your Speck House. Go to your Speck, then head inside the house. You will see a wall panel marked Infinifunnel when you hover it.<br/>
              Go ahead and click it, and it should bring up your Task Board.
              <br/>
              <b>Fun Fact:</b> There's a mini shop in your Infinifunnel that you can use to buy Berry Blast or Popberry Seeds.`,
            },
            {
              type: "image",
              data: {
                img: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-05-04_143226876.png",
                alt: "Infinifunnel",
              },
            },
          ],
        },
        {
          id: "how-many-tasks",
          title: "How Many Tasks Can I Get?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `At a glance, players who are not Land Owners nor VIP get 9 tasks per board. If you are VIP/LO, then you get 12.<br/>
              As you complete tasks, more appear. However, you can only get 40 tasks total per skill.<br/>
              So once you've finished 40 Cooking Tasks, no more will appear. Same for farming, forestry, business, animal care, etc. <br/><br/>
              
              Now the tricky part about this is how many tasks you've actually unlocked at your level.<br/>
              Lower Levels will have less tasks "unlocked" or less lower level tasks unlocked. They can still obtain higher tier tasks, so the amount unlocked is a question mark.<br/>
              So you may not ever reach 40 or you might max out your tasks sooner than you expect.`,
            },
          ],
        },
        {
          id: "same-skill-tasks",
          title: "Same Skill Task Limit",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `You can never get more than 4 tasks of the same skill type.<br/>
              This was done to avoid flooding your Task Board with 9-12 cooking task blockers which is great!<br/>
              So now, you'll only see 4 Forestry Tasks at a time, or 4 Cooking Tasks at a time. You don't have a limit of 4 of those tasks, but it's just a limit on what's visible per board.`,
            },
          ],
        },
        {
          id: "task-board-colors",
          title: "Task Board Colors",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `There are 3 Colors on the Task Board. Each of them signify the type of task you're getting:<br/>
              ⬜ - Plain Tasks are F2P or tasks for everyone. Basically these tasks have no criteria<br/>
              🟦 - Blue Tasks are VIP - only VIP or Land Owners can get Blue Tasks. They are more rare and higher chance of getting $PIXEL<br/>
              🟫 - Brown Tasks are Land Owner Tasks. These are only $PIXEL tasks and very rare to appear`,
            },
          ],
        },
      ],
    },
    {
      id: "merchantships",
      title: "Merchant Fleet",
      type: "section",
      content: [
        {
          id: "what-is-merchantfleet",
          title: "What is Merchant Fleet?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `🚢 The Merchant Fleet is basically a bigger task board. The tasks are grander and are designed for higher level players<br/>
              <em>What is a higher leveled player?</em> Based on the Merchant Fleet or Ships, it seems to be between <b>total levels 150 - 500</b><br/><br/>
              Merchant Fleet Contracts reset every 24 hours, and unlike the Task Board, there doesn't seem to be any limits.
            `,
            },
          ],
        },
        {
          id: "when-unlock",
          title: "When do I unlock Merchant Ship Contracts?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `You unlock Merchant Ship Contracts when you have 50 total levels across all your skills. (So for eg, Level 10 Farming, Level 10 Forestry, Level 10 Mining, Level 10 Stoneshaping, Level 10 Metalworking is enough to unlock Merchant Ship Contracts)`,
            },
          ],
        },
        {
          id: "how-many-tasks",
          title: "How Many Contracts do I get?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `You can only unlock a total of 7 contracts. There's no differentiation for VIP or Land Owners`,
            },
            {
              type: "table",
              data: [
                {
                  Level: 50,
                  Contracts: 2,
                },
                {
                  Level: 101,
                  Contracts: 3,
                },
                {
                  Level: 150,
                  Contracts: 4,
                },
                {
                  Level: 250,
                  Contracts: 5,
                },
                {
                  Level: 300,
                  Contracts: 6,
                },
                {
                  Level: 350,
                  Contracts: 7,
                },
              ],
            },
          ],
        },
        {
          id: "contract-refreshing",
          title: "What is a Contract Refresh?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `A contract refresh is where you pay $PIXEL to change all of the <b>Pending</b> Contracts. So all of the ones you haven't completed yet.<br/>
              The number of contracts you have incomplete doesn't change the price of the refresh <br/> So refreshing all 7 contracts and refreshing 1 contract cost the same amount<br/><br/>
              💎 You can only refresh 3 times. <br/>
              💎 The first contract refresh costs 50 $PIXEL<br/>
              💎 The second contract refresh costs 100 $PIXEL<br/>
              💎 The third contract refresh costs 150 $PIXEL<br/>`,
            },
          ],
        },
        {
          id: "contract-timing-1",
          title: "How long between contracts?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `Each completed task will take appoximately 2 hours.<br/>
              🦜 It gets "sent off" for 2 hours, and when it comes back, you can claim the reward.<br/>
              🪙 Once claimed, it takes 30 seconds before a new contract will appear.`,
            },
          ],
        },
        {
          id: "contract-timing-2",
          title: "What happens if I complete a task close to the reset time?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `Nothing! The other contracts will refresh, and your pending contracts will remain pending on the same timing as before.<br/>
              Once it comes back and you claim the reward, then you get a new contract.`,
            },
          ],
        },
        {
          id: "possible-prizes",
          title: "What can I win?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `Each contract shows the expected prizes for each one, but there's nothing guaranteed except for the EXP and the Buoy Bucks.<br/>
              Each Contract has the following Guaranteed Rewards:<br/>
              🛟 Buoy Bucks (amount depends on the contract)<br/>
              🛟 Business EXP (amount shown on the contract)<br/>
              <br/>
              There's also a chance to receive:<br/>
              💎 $PIXEL<br/>
              📦 Old Salt Strongbox<br/>
              📦 Can-Can Can`,
            },
            {
              type: "text",
              data: `The Old Salt Strongbox is an EXP Gacha Chest. The expected rewards are EXP Potions.<br/>
              Possibilities are Woodworking, Business, Animal Care, Stoneshaping, Cooking, Metalworking, Farming, Mining and Forestry Potions.<br/>
              There's 3 different tiers of Potions<br/>
              <i class="fa-solid fa-flask-vial" style="color: #bdc2cc;"></i> Tier 1: 1,000 EXP<br/>
              <i class="fa-solid fa-flask-vial" style="color: #bdc2cc;"></i> Tier 2: 5,000 EXP<br/>
              <i class="fa-solid fa-flask-vial" style="color: #bdc2cc;"></i> Tier 3: 15,000 EXP<br/>`,
            },
            {
              type: "inlineLink",
              data: {
                text: [
                  "Apiary Tiki Sculpture",
                  "Slug Hutch Tiki Sculpture",
                  "Coop Tiki Sculpture",
                  "Mining Tiki Sculpture",
                  "Forestry Tiki Sculpture",
                  "Farming Tiki Sculpture",
                  "Red Parrot Decoration",
                  "Green Parrot Decoration",
                  "Blue Parrot Decoration",
                  "Purple Pirate Chest",
                  "Blue Pirate Chest",
                  "Red Pirate Chest",
                ],
                itemId: [
                  "itm_bstA06",
                  "itm_bstA05",
                  "itm_bstA04",
                  "itm_bstA03",
                  "itm_bstA02",
                  "itm_bstA01",
                  "itm_parrot_red",
                  "itm_parrot_green",
                  "itm_parrot_blue",
                  "itm_pirate_chest01",
                  "itm_pirate_chest02",
                  "itm_pirate_chest03",
                ],
                description: `The Can-Can Can Box is the fun gacha! It has possible item prizes. Here's what we've seen so far:<br/>
                🗿 Apiary Tiki Sculpture <br/>
                🗿 Slug Hutch Tiki Sculpture <br/>
                🗿 Coop Tiki Sculpture <br/>
                🗿 Mining Tiki Sculpture <br/>
                🗿 Forestry Tiki Sculpture <br/>
                🗿 Farming Tiki Sculpture <br/>
                🦜 Red Parrot Decoration <br/>
                🦜 Green Parrot Decoration <br/>
                🦜 Blue Parrot Decoration <br/>
                🧰 Blue Pirate Chest <br/>
                🧰 Purple Pirate Chest <br/>
                🧰 Red Pirate Chest <br/>
                `,
              },
            },
          ],
        },
        {
          id: "seaside-stash",
          title: "Seaside Stash",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `So where do you spend the <b>Buoy Bucks</b> you've been earning? There is a store to the left of the Harbormaster.<br/>
              <br/>
              🛟 The items rotate every Tuesday at 12 AM UTC<br/>
              🛟 Most of the items appear to be decorative. There's also the Old Salt Strongbox which has been in rotation every cycle so far.`,
            },
            {
              type: "image",
              data: {
                img: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-05-04_153433863.png",
                alt: "Seaside Stash",
              },
            },
          ],
        },
      ],
    },
    {
      id: "fishing",
      title: "Fishing",
      type: "section",
      content: [
        {
          id: "what-is-fishing",
          title: "What is Fishing?",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `🐟 With Merchant Fleet came Fishing. They added fishes to the Pier, but currently not much utility. You can swap a set number of fishes to the Cod Stewart.
            `,
            },
            {
              type: "gallery",
              images: [
                {
                  src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-05-04_154948270.png",
                  alt: "Cod Stewart",
                },
                {
                  src: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-05-04_154937318.png",
                  alt: "Dialog with Cod Stewart",
                },
              ],
            },
          ],
        },
        {
          id: "how-to-fish",
          title: "How to Fish?",
          type: "subdivision",
          content: [
            {
              type: "inlineLink",
              data: {
                text: "Jihoz's Net",
                itemId: "itm_fishingNet",
                description: `🦈 To catch fish, you need Jihoz's Net. You can buy one from the Pet Store.<br/>
                With the net selected, click on a fish shadow in the water and it should go to your inventory. It's that simple.`,
              },
            },
            {
              type: "image",
              data: {
                img: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-05-04_155555688.png",
                alt: "Fishing",
              },
            },
            {
              type: "text",
              data: `The fishes respawn, but the respawn timers are pretty long. I believe they range from 6 hours to 8 hours. And there's so many types of fish to catch (or seaweed)`,
            },
          ],
        },
        {
          id: "cost-of-fish",
          title: "Selling Fish",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `There are 10 Fishes that you can catch + Seaweed. <b>Seaweed</b> is the most common one, so you'll probably get a LOT of that.`,
            },
            {
              type: "image",
              data: {
                img: "https://pixelore.s3.eu-central-003.backblazeb2.com/Guide+Images/image_2025-05-04_160505592.png",
                alt: "selling fish",
              },
            },
            {
              type: "text",
              data: `You can't do anything with the seaweed, but the other fishes can be sold to the Cod Stewart.<br/>
              To sell the fish, select the fish from your inventory, and click on the Cod Stewart. He will give you back 🛟 <b>Buoy Bucks</b> in return.<br/>
              You can only sell 10 Fish Per Day. After the last fish is sold, a timer for 24h appears and you wont be able to sell anymore fish until that timer is up.<br/><br/>
              Here's the prices of each of the fish you can catch:`,
            },

            {
              type: "table",
              data: [
                {
                  Fish: "Seaweed",
                  "🛟": 0,
                },
                {
                  Fish: "Blue Bass",
                  "🛟": 1,
                },
                {
                  Fish: "Cavern Carp",
                  "🛟": 2,
                },
                {
                  Fish: "Pink Pike",
                  "🛟": 3,
                },
                {
                  Fish: "Striped Snapper",
                  "🛟": 4,
                },
                {
                  Fish: "Greenback Grouper",
                  "🛟": 5,
                },
                {
                  Fish: "John Dory",
                  "🛟": 6,
                },
                {
                  Fish: "Tangerine Tang",
                  "🛟": 8,
                },
                {
                  Fish: "Purpleface Piranha",
                  "🛟": 12,
                },
                {
                  Fish: "Clover Cuddlefish",
                  "🛟": 15,
                },
                {
                  Fish: "Mulberry Mullet",
                  "🛟": 30,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export default otherData;
